import { useCallback, useMemo } from 'react';

import { useGetCountriesQuery } from '@/features/app/api';
import { useCurrentUser } from '@/features/users/hooks';
import { CountryOption } from '@/features/insights';

export function useCountries(): {
  options: CountryOption[];
  kv: Record<string, string>;
  map: (value: string) => string | undefined;
} {
  const { data: countries } = useGetCountriesQuery();
  const user = useCurrentUser();

  const options = useMemo(() => {
    if (!countries) return [];
    if (!user) return [];

    return countries.map((country) => ({
      label: country.labels.find((l) => l.language.code === user.language)?.label ?? country.labels[0].label,
      value: country.code,
    }))
    .sort((a, b) => {
      const aOther = a.label.toLowerCase().includes('other');
      const bOther = b.label.toLowerCase().includes('other');
      if (aOther !== bOther) return aOther ? 1 : -1;
      return a.label.localeCompare(b.label);
    });
  }, [countries, user]);

  const kv = useMemo(
    () => options.reduce((map, option) => ({ ...map, [option.value]: option.label }), {} as Record<string, string>),
    [options]
  );

  const map = useCallback((value: string) => kv[value], [kv]);

  return { options, kv, map };
}
